import type {CalypsoCatalog} from "#pricing/catalogs/calypso/catalog"
import {createPriceContainer, createVariantConditionedPriceNodes} from "#pricing/catalogs/graph-helpers"
import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {MathNode} from "#pricing/nodes/math-node"
import {MeshAmountNode} from "#pricing/nodes/mesh-amount-node"
import type {MeshesNode} from "#pricing/nodes/meshes-node"
import type {PriceContainer} from "#pricing/nodes/price-container"

/**
 * The Petra legs are triggered by their meshes, the finish by the furniture finish variant. Every leg is its own mesh placement (two per
 * unit, on the vanity and the tall wall unit), but the catalog sells pairs, so the amount is the number of leg meshes divided by two,
 * rounded up. The vanity uses 430mm legs with the undermount basin and 290mm legs with a sit-on basin; the tall wall unit always uses
 * 430mm legs. Soft Linen has its own SKUs (5960, 5961) and is selected by description like the other finishes.
 */
export function createPetraDecorLegsContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    finishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
): PriceContainer {
    const createLegPairNodes = (length: string, meshName: string) => {
        const pairs = new MathNode({
            parameters: {
                description: `${meshName} / 2, rounded up`,
                a: new MeshAmountNode({parameters: {name: meshName, meshes: Meshes}}),
                b: 2,
                operator: "divide",
                rounding: "ceil",
            },
        })
        return createVariantConditionedPriceNodes(catalog, ["Decor Legs", length], 3, finishConfigMap, pairs)
    }

    const legs430Units = createLegPairNodes("430", "5864-petra-decor-legs-430")
    const legs290Units = createLegPairNodes("290", "5861-petra-decor-legs-290")

    return createPriceContainer("Petra Decor Legs", "Petra Decor Legs", [...legs430Units, ...legs290Units], true)
}
